import React, { useState } from 'react';
import { X, CheckCircle, ArrowLeft, ArrowRight, Upload, Camera } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const AddEmployeeWizard = ({ open, onOpenChange, editMode = false, initialData = null, onSubmit = null }) => {
  const [currentStep, setCurrentStep] = useState(1);
  React.useEffect(() => {
    if (open && editMode && initialData) {
      setFormData(prev => ({
        ...prev,
        firstName: initialData.first_name || initialData.firstName || '',
        lastName: initialData.last_name || initialData.lastName || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        role: initialData.role || 'Caissier',
        baseSalary: initialData.base_salary || initialData.baseSalary || '',
        status: initialData.status || 'Actif',
      }));
    }
  }, [open, editMode, initialData]);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    birthPlace: '',
    nationality: 'Algérienne',
    nationalId: '',
    idType: 'Carte d\'identité nationale',
    idIssueDate: '',
    idExpiryDate: '',
    phone: '',
    email: '',
    address: '',
    
    role: '',
    department: '',
    startDate: '',
    contractType: '',
    workTime: '',
    status: '',
    baseSalary: '',
    paymentFrequency: 'Mensuelle',
    endDate: '',
    bonuses: [{ name: 'Prime de rendement', type: 'Prime', amount: '5 000,00', frequency: 'Mensuelle' }],

    socialSecurityNum: '',
    socialSecurityOrg: 'CNAS',
    affiliationStatus: 'Affilié',
    affiliationDate: '',
    cnasAffiliationNum: '',
    mutuelle: 'Aucune',
    emergencyContactName: '',
    emergencyContactRelation: '',
    emergencyContactPhone: '',
    documents: [],

    paymentMethod: 'cash',
    bank: '',
    accountNumber: '',
    accountHolder: '',
    agency: 'Setif - 00616',
    rib: '67',
    bic: 'BNAGDZAL',
    ccp: '',
    observations: ''
  });

  if (!open) return null;

  const stepperLabels = [
    { step: 1, label: 'Identité', sub: 'Informations personnelles' },
    { step: 2, label: 'Emploi', sub: 'Fonction et contrat' },
    { step: 3, label: 'Administratif', sub: 'Informations sociales' },
    { step: 4, label: 'Paiement', sub: 'Coordonnées bancaires' }
  ];

  const handleNext = () => {
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit(formData);
    } else {
      // Temporary fallback
      console.log("Submitting formData:", formData);
    }
    // Submit logic
    onOpenChange(false);
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-violet-600">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <line x1="19" y1="8" x2="19" y2="14"></line>
                <line x1="22" y1="11" x2="16" y2="11"></line>
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 leading-tight">{editMode ? 'Modifier l\'employé' : 'Ajouter un employé'}</h2>
              <p className="text-sm text-gray-500 font-medium">Étape {currentStep} sur 4 - {stepperLabels[currentStep-1].label}</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Stepper */}
        <div className="flex-shrink-0 px-8 py-6 border-b border-gray-100">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {stepperLabels.map(({ step, label, sub }, index) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center gap-2 relative z-10 bg-white px-2">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all shadow-sm ${
                    step < currentStep ? 'bg-violet-600 text-white' : 
                    step === currentStep ? 'bg-violet-600 text-white ring-4 ring-violet-100' : 
                    'bg-gray-100 text-gray-400'
                  }`}>
                    {step < currentStep ? <CheckCircle size={24} className="text-white" /> : step}
                  </div>
                  <div className="text-center">
                    <span className={`block text-sm font-bold ${step <= currentStep ? 'text-violet-700' : 'text-gray-500'}`}>{label}</span>
                    <span className={`block text-xs ${step <= currentStep ? 'text-violet-500' : 'text-gray-400'}`}>{sub}</span>
                  </div>
                </div>
                {index < 3 && (
                  <div className={`flex-1 h-1 mx-2 rounded-full transform -translate-y-4 ${
                    step < currentStep ? 'bg-violet-600' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          
          {/* STEP 1: Identité */}
          {currentStep === 1 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="flex items-start gap-3 p-4 bg-teal-50 rounded-xl border border-teal-100">
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-teal-700 font-bold">1</span>
                </div>
                <div>
                  <h3 className="font-semibold text-teal-900">Informations personnelles</h3>
                  <p className="text-teal-700 text-sm">Renseignez les informations d'identité de l'employé.</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nom *</label>
                  <Input id="lastName" name="lastName" value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} placeholder="Nom" className="h-11" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Prénom *</label>
                  <Input id="firstName" name="firstName" value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} placeholder="Asma" className="h-11" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date de naissance *</label>
                  <Input type="date" id="birthDate" name="birthDate" value={formData.birthDate} onChange={(e) => setFormData({...formData, birthDate: e.target.value})} className="h-11" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Lieu de naissance</label>
                  <Input id="birthPlace" name="birthPlace" value={formData.birthPlace} onChange={(e) => setFormData({...formData, birthPlace: e.target.value})} placeholder="Alger" className="h-11" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nationalité *</label>
                  <select id="nationality" name="nationality" value={formData.nationality} onChange={(e) => setFormData({...formData, nationality: e.target.value})} className="w-full h-11 px-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent">
                    <option>Algérienne</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">N° pièce d'identité *</label>
                  <Input id="nationalId" name="nationalId" value={formData.nationalId} onChange={(e) => setFormData({...formData, nationalId: e.target.value})} placeholder="123456789012" className="h-11" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Type de pièce</label>
                  <select id="idType" name="idType" value={formData.idType} onChange={(e) => setFormData({...formData, idType: e.target.value})} className="w-full h-11 px-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent">
                    <option>Carte d'identité nationale</option>
                    <option>Passeport</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date d'émission</label>
                  <Input type="date" id="idIssueDate" name="idIssueDate" value={formData.idIssueDate} onChange={(e) => setFormData({...formData, idIssueDate: e.target.value})} className="h-11" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date d'expiration</label>
                  <Input type="date" id="idExpiryDate" name="idExpiryDate" value={formData.idExpiryDate} onChange={(e) => setFormData({...formData, idExpiryDate: e.target.value})} className="h-11" />
                </div>
                
                <div className="col-span-2 pt-4 border-t border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-4">Coordonnées</h4>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Téléphone</label>
                      <Input id="phone" name="phone" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+213 5 55 12 34 56" className="h-11" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email (optionnel)</label>
                      <Input type="email" id="email" name="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="asma.belekhal@email.com" className="h-11" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse</label>
                      <Input id="address" name="address" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="Cité 1200 Logements, Bab Ezzouar, Alger" className="h-11" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Emploi */}
          {currentStep === 2 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-xl border border-orange-100">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-orange-700 font-bold">2</span>
                </div>
                <div>
                  <h3 className="font-semibold text-orange-900">Informations sur l'emploi</h3>
                  <p className="text-orange-700 text-sm">Renseignez les informations liées à l'emploi et au contrat.</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Fonction *</label>
                  <select id="role" name="role" value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} className="w-full h-11 px-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent">
                    <option value="" disabled>Sélectionnez...</option>
                    <option>Caissier</option>
                    <option>Pharmacien</option>
                    <option>Vendeur</option>
                    <option>Préparateur en pharmacie</option>
                    <option>Comptable</option>
                    <option>Manager</option>
                    <option>Autre</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Département / Service</label>
                  <select id="department" name="department" value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} className="w-full h-11 px-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent">
                    <option value="" disabled>Sélectionnez...</option>
                    <option>Caisse</option>
                    <option>Vente / Comptoir</option>
                    <option>Stock / Achat</option>
                    <option>Comptabilité</option>
                    <option>Direction</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date de début d'emploi *</label>
                  <Input type="date" id="startDate" name="startDate" value={formData.startDate} onChange={(e) => setFormData({...formData, startDate: e.target.value})} className="h-11" />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Type de contrat *</label>
                  <select id="contractType" name="contractType" value={formData.contractType} onChange={(e) => setFormData({...formData, contractType: e.target.value})} className="w-full h-11 px-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent">
                    <option value="" disabled>Sélectionnez...</option>
                    <option>CDI - Contrat à durée indéterminée</option>
                    <option>CDD - Contrat à durée déterminée</option>
                    <option>Stagiaire</option>
                    <option>Apprenti</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Temps de travail *</label>
                  <select id="workTime" name="workTime" value={formData.workTime} onChange={(e) => setFormData({...formData, workTime: e.target.value})} className="w-full h-11 px-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent">
                    <option value="" disabled>Sélectionnez...</option>
                    <option>Temps plein</option>
                    <option>Temps partiel</option>
                    <option>Forfait</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Statut *</label>
                  <select id="status" name="status" value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="w-full h-11 px-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent font-medium text-emerald-700 bg-emerald-50">
                    <option value="" disabled>Sélectionnez...</option>
                    <option>Actif</option>
                    <option>Inactif</option>
                    <option>En congé</option>
                    <option>Suspendu</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Salaire de base (DZD) *</label>
                  <Input type="number" id="baseSalary" name="baseSalary" value={formData.baseSalary} onChange={(e) => setFormData({...formData, baseSalary: e.target.value})} placeholder="45 000,00" className="h-11" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Périodicité de paiement *</label>
                  <select id="paymentFrequency" name="paymentFrequency" value={formData.paymentFrequency} onChange={(e) => setFormData({...formData, paymentFrequency: e.target.value})} className="w-full h-11 px-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent">
                    <option>Mensuelle</option>
                    <option>Bimensuelle</option>
                    <option>Hebdomadaire</option>
                    <option>Journalière</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date de fin de contrat</label>
                  <Input type="date" id="endDate" name="endDate" value={formData.endDate} onChange={(e) => setFormData({...formData, endDate: e.target.value})} className="h-11" placeholder="Laissez vide si CDI" />
                  <p className="text-xs text-gray-500 mt-1">Laissez vide si contrat à durée indéterminée</p>
                </div>
              </div>

              <div className="pt-6 border-t border-gray-100">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold text-gray-900 text-lg text-orange-600">Primes & Indemnités</h4>
                  <Button 
                    variant="ghost" 
                    onClick={() => {
                      setFormData({
                        ...formData, 
                        bonuses: [...formData.bonuses, { name: '', type: 'Prime', amount: '', frequency: 'Mensuelle' }]
                      });
                    }}
                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                  >
                    + Ajouter une prime / indemnité
                  </Button>
                </div>
                
                <table className="w-full text-sm">
                  <thead className="text-gray-500 border-b border-gray-200">
                    <tr>
                      <th className="text-left font-medium pb-2 w-1/3">NOM</th>
                      <th className="text-left font-medium pb-2">TYPE</th>
                      <th className="text-left font-medium pb-2">MONTANT</th>
                      <th className="text-left font-medium pb-2">PÉRIODICITÉ</th>
                      <th className="text-center font-medium pb-2">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.bonuses.map((bonus, idx) => (
                      <tr key={idx} className="border-b border-gray-50">
                        <td className="py-3 pr-2">
                          <Input 
                            value={bonus.name} 
                            onChange={(e) => {
                              const newBonuses = [...formData.bonuses];
                              newBonuses[idx].name = e.target.value;
                              setFormData({...formData, bonuses: newBonuses});
                            }} 
                            placeholder="Nom de la prime"
                            className="h-9" 
                          />
                        </td>
                        <td className="py-3 px-2">
                          <select 
                            value={bonus.type} 
                            onChange={(e) => {
                              const newBonuses = [...formData.bonuses];
                              newBonuses[idx].type = e.target.value;
                              setFormData({...formData, bonuses: newBonuses});
                            }} 
                            className="w-full h-9 px-2 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                          >
                            <option>Prime</option>
                            <option>Indemnité</option>
                            <option>Avantage en nature</option>
                          </select>
                        </td>
                        <td className="py-3 px-2">
                          <Input 
                            value={bonus.amount} 
                            onChange={(e) => {
                              const newBonuses = [...formData.bonuses];
                              newBonuses[idx].amount = e.target.value;
                              setFormData({...formData, bonuses: newBonuses});
                            }} 
                            placeholder="Montant"
                            className="h-9 text-right" 
                          />
                        </td>
                        <td className="py-3 px-2">
                          <select 
                            value={bonus.frequency} 
                            onChange={(e) => {
                              const newBonuses = [...formData.bonuses];
                              newBonuses[idx].frequency = e.target.value;
                              setFormData({...formData, bonuses: newBonuses});
                            }} 
                            className="w-full h-9 px-2 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                          >
                            <option>Mensuelle</option>
                            <option>Trimestrielle</option>
                            <option>Semestrielle</option>
                            <option>Annuelle</option>
                            <option>Ponctuelle</option>
                          </select>
                        </td>
                        <td className="py-3 text-center">
                          <button 
                            onClick={() => {
                              const newBonuses = formData.bonuses.filter((_, i) => i !== idx);
                              setFormData({...formData, bonuses: newBonuses});
                            }}
                            className="text-red-500 hover:text-red-700 p-2 transition-colors rounded-md hover:bg-red-50"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* STEP 3: Administratif */}
          {currentStep === 3 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="flex items-start gap-3 p-4 bg-sky-50 rounded-xl border border-sky-100">
                <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sky-700 font-bold">3</span>
                </div>
                <div>
                  <h3 className="font-semibold text-sky-900">Informations administratives & sociales</h3>
                  <p className="text-sky-700 text-sm">Renseignez les informations administratives et de protection sociale.</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-6">
                <div className="col-span-1">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">N° Sécurité sociale (CNAS) *</label>
                  <Input id="socialSecurityNum" name="socialSecurityNum" value={formData.socialSecurityNum} onChange={(e) => setFormData({...formData, socialSecurityNum: e.target.value})} placeholder="1983 07 123456789012" className="h-11" />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Organisme de sécurité sociale *</label>
                  <select id="socialSecurityOrg" name="socialSecurityOrg" value={formData.socialSecurityOrg} onChange={(e) => setFormData({...formData, socialSecurityOrg: e.target.value})} className="w-full h-11 px-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent">
                    <option>CNAS</option>
                    <option>CASNOS</option>
                    <option>CNR</option>
                    <option>Autre</option>
                  </select>
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Situation d'affiliation *</label>
                  <select id="affiliationStatus" name="affiliationStatus" value={formData.affiliationStatus} onChange={(e) => setFormData({...formData, affiliationStatus: e.target.value})} className="w-full h-11 px-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent">
                    <option>Affilié</option>
                    <option>Non affilié</option>
                    <option>En cours</option>
                  </select>
                </div>
                
                <div className="col-span-1">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date d'affiliation *</label>
                  <Input type="date" id="affiliationDate" name="affiliationDate" value={formData.affiliationDate} onChange={(e) => setFormData({...formData, affiliationDate: e.target.value})} className="h-11" />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">N° d'affiliation CNAS</label>
                  <Input id="cnasAffiliationNum" name="cnasAffiliationNum" value={formData.cnasAffiliationNum} onChange={(e) => setFormData({...formData, cnasAffiliationNum: e.target.value})} placeholder="987654321" className="h-11" />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Mutuelle / Assurance complémentaire</label>
                  <select id="mutuelle" name="mutuelle" value={formData.mutuelle} onChange={(e) => setFormData({...formData, mutuelle: e.target.value})} className="w-full h-11 px-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent">
                    <option>Aucune</option>
                    <option>Munatec</option>
                    <option>MACIF</option>
                    <option>MUPAN</option>
                    <option>Autre mutuelle</option>
                  </select>
                </div>
              </div>

              <div className="pt-6 border-t border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-4 text-sky-700">Contact d'urgence</h4>
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Nom du contact *</label>
                    <Input id="emergencyContactName" name="emergencyContactName" value={formData.emergencyContactName} onChange={(e) => setFormData({...formData, emergencyContactName: e.target.value})} placeholder="Nom et prénom" className="h-11" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Lien de parenté *</label>
                    <Input id="emergencyContactRelation" name="emergencyContactRelation" value={formData.emergencyContactRelation} onChange={(e) => setFormData({...formData, emergencyContactRelation: e.target.value})} placeholder="Ex: Mère, Frère..." className="h-11" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Téléphone du contact *</label>
                    <Input id="emergencyContactPhone" name="emergencyContactPhone" value={formData.emergencyContactPhone} onChange={(e) => setFormData({...formData, emergencyContactPhone: e.target.value})} placeholder="+213 5 55 55 55 55" className="h-11" />
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-gray-100">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold text-gray-900 text-sky-700">Documents administratifs</h4>
                  <Button variant="ghost" className="text-sky-600 hover:text-sky-700 hover:bg-sky-50">
                    <Upload className="mr-2 h-4 w-4" /> Sélectionner des fichiers
                  </Button>
                </div>
                
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 flex flex-col items-center justify-center bg-gray-50/50 hover:bg-gray-50 transition-colors mb-4 cursor-pointer">
                  <div className="w-12 h-12 bg-sky-100 text-sky-600 rounded-full flex items-center justify-center mb-3">
                    <Upload size={24} />
                  </div>
                  <p className="font-medium text-gray-900">Glissez vos fichiers ici ou cliquez pour sélectionner</p>
                  <p className="text-sm text-gray-500 mt-1">PDF, JPG ou PNG - Max 5MB par fichier</p>
                </div>
                
                <div className="flex gap-4 flex-wrap">
                  {/* Mock uploaded files */}
                  {['Copie pièce d\'identité.pdf', 'Attestation CNAS.pdf', 'Diplôme.pdf'].map((doc, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
                      <div className="w-8 h-8 rounded bg-red-100 flex items-center justify-center flex-shrink-0 text-red-600 font-bold text-xs">PDF</div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{doc}</p>
                        <p className="text-xs text-gray-500">1.2 MB</p>
                      </div>
                      <button className="text-gray-400 hover:text-red-500 ml-2"><X size={16} /></button>
                    </div>
                  ))}
                  <div className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-600"><Camera size={16} /></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Photo identité.jpg</p>
                      <p className="text-xs text-gray-500">512 KB</p>
                    </div>
                    <button className="text-gray-400 hover:text-red-500 ml-2"><X size={16} /></button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: Paiement */}
          {currentStep === 4 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="flex items-start gap-3 p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-indigo-700 font-bold">4</span>
                </div>
                <div>
                  <h3 className="font-semibold text-indigo-900">Informations de paiement</h3>
                  <p className="text-indigo-700 text-sm">Choisissez le mode de paiement et renseignez les coordonnées bancaires.</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-6">
                {[
                  { id: 'transfer', label: 'Virement bancaire', desc: 'Paiement par virement', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
                  { id: 'cash', label: 'Espèces', desc: 'Paiement en espèces', icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z' },
                  { id: 'check', label: 'Chèque', desc: 'Paiement par chèque', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' }
                ].map((mode) => (
                  <button 
                    key={mode.id}
                    onClick={() => setFormData({...formData, paymentMethod: mode.id})}
                    className={`p-4 rounded-xl border-2 flex items-start gap-4 transition-all text-left ${
                      formData.paymentMethod === mode.id ? 'border-violet-600 bg-violet-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      formData.paymentMethod === mode.id ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-500'
                    }`}>
                      <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={mode.icon}></path></svg>
                    </div>
                    <div>
                      <h4 className={`font-semibold ${formData.paymentMethod === mode.id ? 'text-violet-900' : 'text-gray-900'}`}>{mode.label}</h4>
                      <p className={`text-sm mt-0.5 ${formData.paymentMethod === mode.id ? 'text-violet-700' : 'text-gray-500'}`}>{mode.desc}</p>
                    </div>
                  </button>
                ))}
              </div>

              {formData.paymentMethod === 'transfer' && (
                <div className="grid grid-cols-2 gap-6 p-6 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Banque *</label>
                    <select id="bank" name="bank" value={formData.bank} onChange={(e) => setFormData({...formData, bank: e.target.value})} className="w-full h-11 px-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white">
                      <option value="" disabled>Sélectionnez...</option>
                      <option>BNA - Banque Nationale d'Algérie</option>
                      <option>BEA - Banque Extérieure d'Algérie</option>
                      <option>CPA - Crédit Populaire d'Algérie</option>
                      <option>BADR - Banque de l'Agriculture et du Développement Rural</option>
                      <option>BDL - Banque de Développement Local</option>
                      <option>Société Générale Algérie</option>
                      <option>BNP Paribas El Djazaïr</option>
                      <option>Al Salam Bank</option>
                      <option>Trust Bank Algeria</option>
                      <option>Autre</option>
                    </select>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">N° de compte (RIB) *</label>
                    <Input id="accountNumber" name="accountNumber" value={formData.accountNumber} onChange={(e) => setFormData({...formData, accountNumber: e.target.value})} placeholder="001 00816 0300 0001 2345 67" className="h-11 bg-white" />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Titulaire du compte *</label>
                    <Input id="accountHolder" name="accountHolder" value={formData.accountHolder} onChange={(e) => setFormData({...formData, accountHolder: e.target.value})} placeholder="Nom du titulaire" className="h-11 bg-white" />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Agence *</label>
                    <select id="agency" name="agency" value={formData.agency} onChange={(e) => setFormData({...formData, agency: e.target.value})} className="w-full h-11 px-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white">
                      <option>Sétif - 00616</option>
                    </select>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Clé RIB *</label>
                    <Input id="rib" name="rib" value={formData.rib} onChange={(e) => setFormData({...formData, rib: e.target.value})} placeholder="67" className="h-11 bg-white" />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">BIC / SWIFT (optionnel)</label>
                    <Input id="bic" name="bic" value={formData.bic} onChange={(e) => setFormData({...formData, bic: e.target.value})} placeholder="BNAGDZAL" className="h-11 bg-white" />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">CCP (optionnel)</label>
                  <Input id="ccp" name="ccp" value={formData.ccp} onChange={(e) => setFormData({...formData, ccp: e.target.value})} placeholder="001-1234567-89" className="h-11" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Observations (optionnel)</label>
                  <Input id="observations" name="observations" value={formData.observations} onChange={(e) => setFormData({...formData, observations: e.target.value})} placeholder="Informations complémentaires..." className="h-11" />
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <Button variant="ghost" onClick={currentStep === 1 ? handleClose : handleBack} className="text-gray-600 hover:bg-gray-200">
            {currentStep === 1 ? 'Annuler' : <><ArrowLeft className="mr-2 h-4 w-4" /> Étape précédente</>}
          </Button>
          
          <Button 
            onClick={currentStep === 4 ? handleSubmit : handleNext} 
            className="bg-violet-600 hover:bg-violet-700 text-white px-8 h-11"
          >
            {currentStep === 4 ? (editMode ? 'Enregistrer les modifications' : 'Créer l\'employé') : <><span className="mr-2">Étape suivante</span> <ArrowRight className="h-4 w-4" /></>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddEmployeeWizard;
